// CFM auto underwriting questions (GitHub issue #32744; parent #32176 "CFM - Questions").
//
// Mirrors the CFM FARM UW pattern exactly: a single dedicated pre-bind screen
// (offer/components/pre-bind-auto-UW) renders these as sectioned Yes/No cards with a conditional
// details textarea, closed by ONE agent attestation. Everything is captured on
// offer.quote.preBindUWCheck.autoVerification for MANUAL underwriter review — answers never
// auto-decline the offer.
//
// Per the CFM spreadsheet the questions were CONSOLIDATED from per-driver / per-vehicle prompts into
// policy-level "any driver / any vehicle …" questions; when the answer needs elaboration, the single
// details box collects the specifics (which driver/vehicle, dates, amounts) guided by `label`.
// Dropped from the sheet: "Is your primary homeowner policy with CFM?" (redundant — matching carrier
// is derivable) and the per-vehicle "solely owned / registered to applicant" question (already
// captured as solelyOwnedByApplicant + jointOwner on the vehicle by the CFM auto product #32447).
//
// Copy follows the Branch technical style guide (plain language, sentence case, Oxford comma,
// contractions, numbers 1–9 spelled out); the underwriting MEANING of each question is unchanged.
//
// Answers persist as Booleans (schema Boolean, matching farm/FL UW): the shared radio field
// converts its 'true'/'false' option values to real booleans on change. Details are free-text
// Strings; the attestation is a Boolean.
//
// Question config shape (consumed by the reused FarmUWQuestionsCard on the pre-bind auto UW screen):
//   { id, question, tooltip?, label?, showDetailsWhen? }
// - showDetailsWhen defaults to ['true', true] (details textarea shown + required on "Yes").
// - label is the details-textarea prompt / help text.

export const driverQuestions = [
  {
    id: 'anyDriverLicenseSuspendedOrRevoked',
    question: 'Has any driver had their license suspended or revoked in the last five years?',
    label: 'List each driver, when it happened, and what happened.'
  },
  {
    id: 'anyDriverUnlistedRecentLoss',
    question:
      "Has any driver had a loss in the last three years that isn't already listed under Accidents and convictions?",
    label: 'List the driver and describe each loss.'
  },
  {
    id: 'anyDriverImpairingCondition',
    question: 'Does any driver have a medical condition that could affect their ability to drive safely?',
    label: 'List the driver and describe the condition.'
  }
];

export const vehicleQuestions = [
  {
    // #35901: the "modified or customized" question was removed — custom equipment is now captured
    // per vehicle on the auto details tab (custom-equipment coverage limit), so asking it here was
    // redundant.
    id: 'anyVehicleExistingDamage',
    question: 'Do any vehicles have existing damage, including cracked or broken glass?',
    label: 'List the vehicle and describe the damage.'
  },
  {
    id: 'anyVehicleUsedForHire',
    question: 'Is any vehicle used for delivery, as a taxi, or for rideshare (Uber, Lyft, and similar)?',
    label: "List the vehicle and describe how it's used."
  }
];

export const policyQuestions = [
  {
    // #35901: the policy-level garaging question was removed — the garaging address is now captured
    // per vehicle on the auto details tab, so asking "are all vehicles garaged at the policy
    // address?" here was redundant.
    id: 'hasUnlistedOrExcludedHouseholdDrivers',
    question:
      'Is anyone age 15 or older in the household, or any regular driver who lives elsewhere, not listed or excluded on this policy?',
    label: 'List each person and explain.'
  }
];

// Single agent attestation at the foot of the form (Boolean). Legal wording — keep verbatim unless
// legal/UW signs off on a change.
export const ATTESTATION_ID = 'signedAutoUWAttestation';
export const ATTESTATION_LABEL =
  'I hereby certify that the answers to the questions listed above are true and accurate to the best of my knowledge.';

// Mirrors farm's getFarmUWQuestionSections — the pre-bind screen and its validation schema build
// from this. Auto has no policy-type-specific filtering today, so it takes no args.
export const getAutoUWQuestionSections = () => ({
  driverQuestions,
  vehicleQuestions,
  policyQuestions
});

// Every question id (both sections and the details fields follow the `${id}Details` convention),
// handy for building initial values / validation without re-walking the sections.
export const allAutoUWQuestions = [...driverQuestions, ...vehicleQuestions, ...policyQuestions];

export default {
  driverQuestions,
  vehicleQuestions,
  policyQuestions,
  allAutoUWQuestions,
  getAutoUWQuestionSections,
  ATTESTATION_ID,
  ATTESTATION_LABEL
};
